import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, CheckCircle, XCircle, Building2, CreditCard } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { WorkRoleMultiSelect } from "@/components/roles/WorkRoleMultiSelect";
import type { WorkRole } from "@/lib/roles/multiRole";

// `roles` sits between verification and provisioning. It is a real stop, not a
// spinner caption: the question has to be answered BEFORE the shop exists,
// because afterwards there is nothing to attach the answer to without a second
// round trip and a half-configured shop in between.
type ProvisioningStep = 'checking' | 'roles' | 'provisioning' | 'checkout' | 'complete' | 'error';


export default function BetaComplete() {
  const navigate = useNavigate();
  // provision-beta-org writes the new shop + OWNER membership rows
  // server-side, but the React-side AuthContext snapshot was taken at
  // signup (before those rows existed). Without a refresh, downstream
  // shop-required routes (e.g. /beta/sign-agreement, /) evaluate
  // isPassportMode=true against the stale userShops=[] and forcibly
  // redirect to /passport with a "Shop required" toast.
  //
  // Must be `refetchUserShops` (full userShops + activeShop +
  // membership reload), NOT `refetchMembership`. `refetchMembership`
  // is gated on `if (user && activeShop)` and only re-fetches the
  // single membership row for an EXISTING active shop — it is a
  // no-op for fresh beta-invite users because `activeShop` is null
  // until userShops is reloaded. See the 2026-05-26 deeper audit.
  //
  // The canonical fallback safety net — the postgres_changes realtime
  // listener on `memberships` at AuthContext L696-723 — only works
  // once `memberships` is in the `supabase_realtime` publication.
  // Ships in the same batch as this fix.
  const { refetchUserShops } = useAuth();
  const [step, setStep] = useState<ProvisioningStep>('checking');
  const [error, setError] = useState<string | null>(null);
  const [shopName, setShopName] = useState<string | null>(null);
  const [isBeta, setIsBeta] = useState(false);

  // ── WHAT THIS OWNER ACTUALLY DOES ────────────────────────────────────────
  //
  // A beta invite carries an admin-approved ACCESS level — OWNER, ORG_ADMIN,
  // MANAGER — and says nothing whatsoever about whether this person turns
  // wrenches, writes repair orders or details cars. Those are different facts
  // and the invite has never held the second one.
  //
  // Empty is the default and a valid final answer. An owner who does no
  // vehicle work has an empty set, and guessing TECHNICIAN to avoid an empty
  // state would put them in the mechanical assignment pool on day one.
  const [workRoles, setWorkRoles] = useState<WorkRole[]>([]);
  const [inviteToken, setInviteToken] = useState<string | null>(null);

  useEffect(() => {
    startProvisioning();
  }, []);

  const startProvisioning = async () => {
    try {
      // Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // Redirect to auth with beta flag
        const betaData = localStorage.getItem('beta_invite_data');
        if (betaData) {
          const { email } = JSON.parse(betaData);
          navigate(`/auth?mode=signup&beta=1&email=${encodeURIComponent(email)}`);
        } else {
          navigate('/auth');
        }
        return;
      }

      // Get stored invite data
      const betaDataStr = localStorage.getItem('beta_invite_data');
      if (!betaDataStr) {
        setError("No invite data found. Please use your invite link again.");
        setStep('error');
        return;
      }

      const betaData = JSON.parse(betaDataStr);

      // FIX-R130-HIGH-3: verify the localStorage invite is actually for the
      // currently-signed-in session. Without this, an abandoned invite from
      // user A still sitting in localStorage would be consumed by user B if
      // they visit /beta/complete (e.g., direct nav, stale tab). The
      // server-side provision-beta-org also re-checks the invite against the
      // bearer token, but doing it client-side gives a clear UX message
      // and avoids polluting the audit log with a misattributed call.
      const sessionEmail = (session.user.email || '').toLowerCase().trim();
      const inviteEmail = String(betaData.email || '').toLowerCase().trim();
      if (!inviteEmail || sessionEmail !== inviteEmail) {
        try { localStorage.removeItem('beta_invite_data'); } catch { /* best-effort; ignore */ }
        setError(
          `This invite was issued to ${inviteEmail || 'a different address'}, ` +
          `but you're signed in as ${sessionEmail}. Sign out and use the invite link again.`
        );
        setStep('error');
        return;
      }

      setShopName(betaData.shop_name);

      // Stop and ask. Everything above this line is verification; everything
      // below it creates rows.
      setInviteToken(betaData.token);
      setStep('roles');
      return;

    } catch (err: any) {
      console.error('Beta complete error:', err);
      setError(err.message || 'An unexpected error occurred');
      setStep('error');
    }
  };

  /**
   * Provisioning proper, resumed once the work-role question is answered.
   *
   * The token is carried in state rather than re-read from localStorage so
   * that the invite this provisions is unambiguously the one that was
   * verified above — including the signed-in-as check.
   */
  const completeProvisioning = async (token: string, roles: WorkRole[]) => {
    try {
      setStep('provisioning');

      // Call provision-beta-org
      //
      // The work roles go to the SERVER, which holds the service-role key and
      // is the only thing allowed to write `membership_work_roles`. The client
      // stating them here is a request, not a grant: provisioning validates
      // the vocabulary and applies them to the membership it just created.
      const { data: provisionData, error: provisionError } = await supabase.functions.invoke('provision-beta-org', {
        body: { token, work_roles: roles }
      });

      if (provisionError) {
        console.error('Provisioning error:', provisionError);
        throw new Error(provisionError.message || 'Failed to set up your shop');
      }

      if (provisionData.error) {
        throw new Error(provisionData.error);
      }

      const shopId = provisionData.shop_id;
      const shopIsBeta = !!provisionData.is_beta;
      setIsBeta(shopIsBeta);
      console.log('Shop provisioned:', shopId, provisionData);

      // Clear the beta invite data
      localStorage.removeItem('beta_invite_data');

      // Full userShops + activeShop reload after provision-beta-org
      // — NOT just the single-row activeShop membership refresh that
      // refetchMembership would have done. Without this, the
      // ProtectedRoute on /beta/sign-agreement evaluates
      // isPassportMode=true against the stale userShops=[] taken at
      // signup time (before provision-beta-org inserted the OWNER
      // membership) and forcibly redirects to /passport with a "Shop
      // required" toast.
      //
      // `refetchUserShops` invokes the canonical `loadUserShops`
      // path, which queries memberships fresh, builds userShops,
      // sets activeShop, runs the membership scope resolver, and
      // updates schoolMembershipCount. On resolve, isPassportMode
      // flips false and usePermissions().role exposes the new OWNER.
      //
      // Awaited (not fire-and-forget) so the 1500ms toast delay +
      // navigate below run against fresh AuthContext state. Failure
      // is non-fatal — log and continue; worst case the user falls
      // back to the prior /passport redirect and can navigate
      // manually.
      try {
        await refetchUserShops();
      } catch (refreshErr) {
        console.warn('[BetaComplete] refetchUserShops after provisioning failed:', refreshErr);
      }

      // Show success toast
      if (provisionData.already_provisioned) {
        toast.info('Your shop is already set up!');
      } else if (provisionData.repaired) {
        // A previous attempt claimed this shop and did not finish; this call
        // completed it. Same shop, same membership — worth saying so rather
        // than announcing a creation that happened earlier.
        toast.success('Finished setting up your shop!');
      } else {
        toast.success('Shop created successfully!');
      }

      // Beta shops skip checkout — go straight to agreement signing
      if (shopIsBeta) {
        setStep('complete');
        toast.success('Welcome to the beta! Redirecting...');
        setTimeout(() => navigate('/beta/sign-agreement'), 1500);
        return;
      }

      setStep('checkout');

      // Create Stripe checkout session
      const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke('stripe-create-checkout', {
        body: {
          shop_id: shopId,
          plan_key: 'core',
          success_url: `${window.location.origin}/billing/success`,
          cancel_url: `${window.location.origin}/billing`,
        }
      });

      if (checkoutError) {
        console.error('Checkout error:', checkoutError);
        throw new Error(checkoutError.message || 'Failed to create checkout session');
      }

      if (checkoutData?.url) {
        // Redirect to Stripe Checkout
        window.location.href = checkoutData.url;
      } else {
        throw new Error('No checkout URL returned');
      }

    } catch (err: any) {
      console.error('Beta complete error:', err);
      setError(err.message || 'An unexpected error occurred');
      setStep('error');
    }
  };

  const handleRetry = () => {
    setError(null);
    setStep('checking');
    startProvisioning();
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@optibayai.com';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/50 p-4">
      {/* Wider on a tablet so the work-role question can use two columns;
          the phone layout is unchanged. */}
      <Card className="w-full max-w-md sm:max-w-lg">
        <CardHeader className="text-center">
          {step === 'error' ? (
            <>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <XCircle className="h-6 w-6" />
              </div>
              <CardTitle>Setup Failed</CardTitle>
              <CardDescription>We couldn't complete your setup</CardDescription>
            </>
          ) : (
            <>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                {step === 'checkout' ? (
                  <CreditCard className="h-6 w-6 text-primary" />
                ) : (
                  <Building2 className="h-6 w-6 text-primary" />
                )}
              </div>
              <CardTitle>
                {step === 'checking' && 'Checking your account...'}
                {step === 'roles' && 'One quick question'}
                {step === 'provisioning' && 'Setting up your shop...'}
                {step === 'checkout' && 'Redirecting to checkout...'}
                {step === 'complete' && 'Setup Complete!'}
              </CardTitle>
              <CardDescription>
                {step === 'checking' && 'Verifying your authentication'}
                {step === 'roles' && (shopName ? `Before we create ${shopName}` : 'Before we create your shop')}
                {step === 'provisioning' && (shopName ? `Creating ${shopName}` : 'Creating your shop')}
                {step === 'checkout' && 'Taking you to payment'}
                {step === 'complete' && 'Your shop is ready'}
              </CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {step === 'error' ? (
            <>
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={handleContactSupport}>
                  Contact Support
                </Button>
                <Button className="flex-1" onClick={handleRetry}>
                  Try Again
                </Button>
              </div>
            </>
          ) : step === 'roles' ? (
            <div className="space-y-5">
              <WorkRoleMultiSelect
                value={workRoles}
                onChange={setWorkRoles}
                label="What do you do in the shop?"
                description="Your access level is already set by your invite — this is about the work you personally perform. Choose as many as apply, or none if you do not work on vehicles yourself."
                idPrefix="beta-work-role"
              />
              <Button
                className="w-full"
                data-testid="beta-roles-continue"
                onClick={() => inviteToken && completeProvisioning(inviteToken, workRoles)}
                disabled={!inviteToken}
              >
                Continue
              </Button>
              <p className="text-xs text-muted-foreground">
                You can change this at any time from Team Management.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Progress steps */}
              <div className="space-y-3">
                <ProgressStep 
                  label="Verify account" 
                  status={step === 'checking' ? 'loading' : 'complete'} 
                />
                <ProgressStep 
                  label="Create shop" 
                  status={
                    ['checking', 'roles'].includes(step) ? 'pending' :
                    step === 'provisioning' ? 'loading' : 'complete'
                  } 
                />
                {!isBeta && (
                  <ProgressStep 
                    label="Start subscription" 
                    status={
                      ['checking', 'roles', 'provisioning'].includes(step) ? 'pending' :
                      step === 'checkout' ? 'loading' : 'complete'
                    } 
                  />
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ProgressStep({ label, status }: { label: string; status: 'pending' | 'loading' | 'complete' }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-6 w-6 items-center justify-center">
        {status === 'pending' && (
          <div className="h-2 w-2 rounded-full bg-muted-foreground/30" />
        )}
        {status === 'loading' && (
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
        )}
        {status === 'complete' && (
          <CheckCircle className="h-4 w-4 text-green-500" />
        )}
      </div>
      <span className={status === 'pending' ? 'text-muted-foreground' : ''}>
        {label}
      </span>
    </div>
  );
}
